import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSalesLensItemMetricCard } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { SalesLensItemMetricCardModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'
import { getDateRange } from '@/utils/date'

type TrendDataModel<T> = {
  prevMetricsMonthly: T
  prevMetricsYearly: T
}

export default function useSalesLensItemMetricCard(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string,
  size: string,
  temperature: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [salesLensItemMetricCardData, setSalesLensItemMetricCardData] =
    useState<SalesLensItemMetricCardModel>()
  const [trendData, setTrendData] =
    useState<TrendDataModel<SalesLensItemMetricCardModel>>()

  const fetchAllData = useCallback(async () => {
    if (!itemGroupName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchCurrentData(), fetchTDData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [branches, startDate, endDate, itemGroupName, size, temperature])

  const fetchCurrentData = useCallback(async () => {
    const { data } = await fetchSalesLensItemMetricCardData(
      branches,
      startDate,
      endDate,
      itemGroupName,
      size,
      temperature
    )
    setSalesLensItemMetricCardData(data)
  }, [branches, startDate, endDate, itemGroupName, size, temperature])

  const fetchTDData = useMemo(() => {
    return async () => {
      try {
        // TODO: Store metrics data in localStorage
        // const today = new Date()
        const today = new Date('2024-07-27')
        const formattedDate = today.toISOString().split('T')[0]

        const mtdStorageKey = `MTD-salesLens-metricCard-${itemGroupName}-${size}-${temperature}`
        const ytdStorageKey = `YTD-salesLens-metricCard-${itemGroupName}-${size}-${temperature}`
        const storedMtdData = localStorage.getItem(mtdStorageKey)
        const storedYtdData = localStorage.getItem(ytdStorageKey)

        // Check if stored data is from today
        if (storedMtdData && storedYtdData) {
          try {
            const mtdData = JSON.parse(storedMtdData)
            const ytdData = JSON.parse(storedYtdData)

            if (
              mtdData.date !== formattedDate ||
              ytdData.date !== formattedDate ||
              mtdData.branches !== branches ||
              ytdData.branches !== branches ||
              mtdData.itemGroupName !== itemGroupName ||
              mtdData.size !== size ||
              mtdData.temperature !== temperature
            ) {
              await setLocalTrendingData(
                branches,
                formattedDate,
                mtdStorageKey,
                ytdStorageKey,
                itemGroupName,
                size,
                temperature,
                setTrendData
              )
            } else {
              setTrendData({
                prevMetricsMonthly: mtdData.companyMetricsForTrending,
                prevMetricsYearly: ytdData.companyMetricsForTrending,
              })
              return
            }
          } catch (err) {
            setError(
              new Error(
                err instanceof Error ? err.message : 'Error: Stored data'
              )
            )
          }
        } else {
          await setLocalTrendingData(
            branches,
            formattedDate,
            mtdStorageKey,
            ytdStorageKey,
            itemGroupName,
            size,
            temperature,
            setTrendData
          )
        }
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), itemGroupName, size, temperature])

  const refetch = useCallback(() => {
    console.log('Refetching sales lens item metric card...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    salesLensItemMetricCardData,
    trendData,
    loading,
    error,
    refetch,
  }
}

async function fetchSalesLensItemMetricCardData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string,
  size: string,
  temperature: string
): Promise<{
  data: SalesLensItemMetricCardModel
}> {
  try {
    const response = await getSalesLensItemMetricCard(
      branches,
      startDate,
      endDate,
      itemGroupName,
      size,
      temperature
    )

    const data: SalesLensItemMetricCardModel = convertData(
      response.net_sales_amount,
      response.net_sales_percentage,
      response.quantity_sold,
      response.net_to_discount_percentage
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  netSalesAmount: number,
  netSalesPercentage: number,
  quantitySold: number,
  netToDiscountPercentage: number
): SalesLensItemMetricCardModel {
  return {
    netSalesAmount: formatMetric(netSalesAmount, 2),
    netSalesPercentage: formatMetric(netSalesPercentage, 2),
    unitSold: formatMetric(quantitySold, 2),
    avgSellingPrice: formatMetric(netSalesAmount / quantitySold, 2),
    netToDiscountPercentage: formatMetric(netToDiscountPercentage, 2),
  }
}

async function setLocalTrendingData(
  branches: string,
  formattedDate: string,
  mtdStorageKey: string,
  ytdStorageKey: string,
  itemGroupName: string,
  size: string,
  temperature: string,
  setTrendData: (data: TrendDataModel<SalesLensItemMetricCardModel>) => void
) {
  const mtdDate = getDateRange('month-to-date')
  const ytdDate = getDateRange('year-to-date')
  const mtdTrendingDate = mtdDate.trending as DateRange
  const ytdTrendingDate = ytdDate.trending as DateRange

  const getTdMetric = async (args: [string, string]) => {
    const { data } = await fetchSalesLensItemMetricCardData(
      branches,
      ...args,
      itemGroupName,
      size,
      temperature
    )
    return data
  }

  const [
    mCompanyMetrics,
    mtdCompanyMetrics,
    yCompanyMetrics,
    ytdCompanyMetrics,
  ] = await Promise.all([
    getTdMetric([dateToString(mtdDate, 'from'), dateToString(mtdDate, 'to')]),
    getTdMetric([
      dateToString(mtdTrendingDate, 'from'),
      dateToString(mtdTrendingDate, 'to'),
    ]),
    getTdMetric([dateToString(ytdDate, 'from'), dateToString(ytdDate, 'to')]),
    getTdMetric([
      dateToString(ytdTrendingDate, 'from'),
      dateToString(ytdTrendingDate, 'to'),
    ]),
  ])

  const mtdStorageValue = {
    date: formattedDate,
    branches: branches,
    itemGroupName: itemGroupName,
    size: size,
    temperature: temperature,
    companyMetrics: mCompanyMetrics,
    companyMetricsForTrending: mtdCompanyMetrics,
  }
  const ytdStorageValue = {
    date: formattedDate,
    branches: branches,
    itemGroupName: itemGroupName,
    size: size,
    temperature: temperature,
    companyMetrics: yCompanyMetrics,
    companyMetricsForTrending: ytdCompanyMetrics,
  }

  setTrendData({
    prevMetricsMonthly: mtdStorageValue.companyMetricsForTrending,
    prevMetricsYearly: ytdStorageValue.companyMetricsForTrending,
  })

  localStorage.setItem(mtdStorageKey, JSON.stringify(mtdStorageValue))
  localStorage.setItem(ytdStorageKey, JSON.stringify(ytdStorageValue))
}
